<template>
  <v-card elevation="2">
    <v-toolbar flat>
      <v-toolbar-title>
        {{ title ?? $t('activityLog.title') }}
      </v-toolbar-title>

      <v-spacer />

      <div class="d-flex align-center" style="gap: 1rem">
        <v-switch
          v-model="showSystemMessages"
          :label="$t('activityLog.showSystemMessages')"
          hide-details
        />
        <v-btn icon @click="getPDF">
          <v-icon> mdi-file-pdf-outline </v-icon>
        </v-btn>
      </div>
    </v-toolbar>

    <v-card-text>
      <v-timeline
        align-top
        dense
      >
        <v-timeline-item
          :color="$vuetify.theme.dark ? 'grey darken-2' : 'white'"
          fill-dot
        >
          <template #icon>
            <v-icon small :color="$vuetify.theme.dark ? 'grey lighten-2' : 'secondary'">
              {{ entryTemplate.icon }}
            </v-icon>
          </template>
          <v-slide-x-transition mode="out-in">
            <v-card v-if="showNewNote">
              <v-card-text class="text--primary">
                <v-textarea
                  v-model="entryTemplate.text"
                  :label="$t('activityLog.message')"
                  outlined
                  clearable
                  clear-icon="mdi-close-circle"
                  auto-grow
                  autofocus
                  rows="3"
                  @keydown="addEntryKeyDown"
                  hide-details
                ></v-textarea>
                <v-datetime-picker
                  v-if="$store.getters.currentUser.organizationKind == 'Internal'"
                  useIso
                  :label="$t('activityLog.customTimeLabel')"
                  v-model="entryTemplate.entryDateTime"
                  date-format="yyyy-MM-dd"
                  time-format="HH:mm:ss"
                  :time-picker-props="timeProps"
                >
                  <template slot="dateIcon">
                    <v-icon>mdi-calendar</v-icon>
                  </template>
                  <template slot="timeIcon">
                    <v-icon>mdi-clock</v-icon>
                  </template>
                  <template
                    slot="actions"
                    slot-scope="{ parent }"
                  >
                    <v-btn
                      outlined
                      color="primary"
                      @click.native="parent.cancelHandler"
                    >
                      {{ $t('common.cancel') }}
                    </v-btn>
                    <v-btn
                      color="secondary"
                      @click="parent.okHandler"
                    >
                      {{ $t('common.ok') }}
                    </v-btn>
                  </template>
                </v-datetime-picker>
              </v-card-text>
              <v-card-actions>
                <v-checkbox
                  v-if="$store.getters.currentUser.organizationKind == 'Internal'"
                  v-model="entryTemplate.isInternal"
                  dense
                  :label="$t('activityLog.markInternal')"
                />
                <v-spacer />
                <v-btn
                  text
                  @click="showNewNote = false; resetEntryTemplate()"
                >
                  {{ $t('common.cancel') }}
                </v-btn>
                <v-btn
                  color="secondary"
                  :disabled="!entryTemplate.text"
                  :loading="addingNote"
                  outlined
                  @click="addEntry"
                >
                  {{ $t('common.send') }}
                  <v-icon right>
                    mdi-send
                  </v-icon>
                </v-btn>
              </v-card-actions>
            </v-card>
            <v-btn
              v-else
              class="mb-4"
              color="primary"
              outlined
              @click="showNewNote = true"
            >
              {{ $t('activityLog.new') }}
              <v-icon right>
                mdi-plus
              </v-icon>
            </v-btn>
          </v-slide-x-transition>
        </v-timeline-item>

        <v-slide-y-transition group>
          <v-timeline-item
            v-for="(item, i) in computedEntries"
            :key="i"
            :color="$vuetify.theme.dark ? 'grey darken-2' : 'white'"
            fill-dot
          >
            <template #icon>
              <v-icon small :color="$vuetify.theme.dark ? 'grey lighten-2' : 'secondary'">
                {{ item.icon }}
              </v-icon>
            </template>
            <v-card
              :class="{
                'light-blue lighten-5': !$vuetify.theme.dark && item.type === 'Message' && item.createdByOrganization === $store.getters.userOrganizationName,
                'grey lighten-3': !$vuetify.theme.dark && item.type === 'Message' && item.createdByOrganization !== $store.getters.userOrganizationName,
                'light-blue darken-4': $vuetify.theme.dark && item.type === 'Message' && item.createdByOrganization === $store.getters.userOrganizationName,
                'grey darken-3': $vuetify.theme.dark && item.type === 'Message' && item.createdByOrganization !== $store.getters.userOrganizationName,
              }"
            >
              <v-card-title>
                <div class="d-flex justify-space-between align-start" style="width: 100%">
                  <div class="text-body-2">
                    {{ item.title }}
                  </div>
                  <v-icon
                    v-if="item.isInternal"
                    color="secondary lighten-2"
                  >
                    mdi-eye-off-outline
                  </v-icon>
                  <v-icon
                    v-if="item.type !== 'Message'"
                    color="secondary lighten-2"
                  >
                    mdi-robot-outline
                  </v-icon>
                </div>
              </v-card-title>
              <v-card-subtitle v-if="item.createdByUserFullNameWithDetails" class="text-caption">
                {{ item.createdByUserFullNameWithDetails }}
              </v-card-subtitle>
              <v-card-text>
                <ReadMore v-if="item.text" :html="item.text" />
                <div class="d-flex justify-space-between align-end text-caption mt-4 mb-n2 grey--text">
                  <div>{{ getRelationNameById(item.relationId) }}</div>
                  <div>{{ item.entryDateTime | formatDateTime }}</div>
                </div>
              </v-card-text>
            </v-card>
          </v-timeline-item>
        </v-slide-y-transition>
      </v-timeline>
    </v-card-text>
  </v-card>
</template>

<script>
import ReadMore from '@/components/ReadMore.vue'
import fileHandler from '@/mixins/fileHandler';

export default {
  name: 'ActivityLog',
  components: {
    ReadMore
  },
  mixins: [fileHandler],
  props: {
    title: {
      type: String
    },
    addEndpoint: {
      type: String,
      default: 'messages'
    },
    entryTemplate: {
      type: Object,
      default: () => ({
        title: '',
        text: '',
        type: 'Message',
        icon: 'mdi-message-outline',
        entryDateTime: new Date(),
        isInternal: false,
        relationId: null,
        relationKind: null
      })
    },
    entries: {
      type: Array,
      default: () => ([])
    },
    closed: {
      type: Boolean,
      default: false
    },
    relationId: {
      type: String,
      required: true
    },
    relationKind: {
      type: String,
      required: true
    },
    relations: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      showSystemMessages: true,
      showNewNote: false,
      addingNote: false,
      timeProps: {
        format:"24hr",
        useSeconds: true,
      }
    }
  },
  computed: {
    computedEntries() {
      if (!this.showSystemMessages) {
        return this.entries.filter(entry => entry.type === 'Message');
      }

      else {
        return this.entries;
      }
    }
  },
  methods: {
    resetEntryTemplate() {
      this.entryTemplate.text = '';
      this.entryTemplate.entryDateTime = null;
      this.entryTemplate.isInternal = false;
    },
    addEntryKeyDown(e) {
      if (e.key==="Escape") {
        this.resetEntryTemplate();
      }

      if ((e.key === "s" && e.altKey) || (e.keyCode === 13 && e.ctrlKey)) {
        e.preventDefault();
        this.addEntry();
      }
    },
    async addEntry() {
      this.addingNote = true;

      if (this.entryTemplate.hasOwnProperty('relationId')) {
        this.entryTemplate.relationId = this.relationId;
      }

      if (this.entryTemplate.hasOwnProperty('relationKind')) {
        this.entryTemplate.relationKind = this.relationKind;
      }

      try {
        const response = await this.$api.post(this.addEndpoint, this.entryTemplate);

        if (!response.data.success) throw new Error(response.data.errors);

        this.entries.unshift(response.data.model);
        this.showNewNote = false;
        this.resetEntryTemplate();
      } catch (error) {
        console.error(error);
        this.$notifier.showError({ message: this.$t('activityLog.errorSaving') });
      } finally {
        this.addingNote = false;
      }
    },

    getRelationNameById(relationId) {
      if (!this.relations.length) return '';

      const relationsMap = new Map(this.relations);

      return relationsMap.get(relationId);
    },

    async getPDF() {
      try {
        const response = await this.$api.get('messages/generate-pdf', {
          params: {
            relationId: this.relationId,
            relationKind: this.relationKind,
            includeSystemMessages: this.showSystemMessages
          },
          responseType: 'arraybuffer'
        });

        this.streamFile(null, response)
      } catch (error) {
        console.error(error);
        this.$notifier.showError({ message: this.$t('activityLog.errorPdf') });
      }
    }
  }
};
</script>